"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Tabs from "../../components/inventory/modules/Tabs/Tabs"
import Badge from "../../components/inventory/modules/Badge/Badge"
import OrderInventory from "../../components/inventory/order"
import DashboardStats from "../../components/inventory/sections/DashboardStats"
import DashboardMetricsCard from "../../components/inventory/sections/DashboardMetricsCard"
import PendingOrdersSection from "../../components/inventory/sections/PendingOrdersSection"
import DropshipRequestsSection from "../../components/inventory/sections/DropshipRequestsSection"
import styles from "./InventoryDashboardPage.module.css"
import Card from "../../components/inventory/modules/Card/Card"
import { getWarehouseStats } from "../../services/inventory/devices"

const AdminDashboardView = ({ currentUser }) => {
  const navigate = useNavigate()
  const [activeMainTab, setActiveMainTab] = useState("refurbishment")
  const [activeSubTab, setActiveSubTab] = useState("refurbished-devices")
  const [orderInventoryModal, setOrderInventoryModal] = useState({ isOpen: false })

  // ---------------------------
  // Dynamic KPI state (replaces static KPIs)
  // ---------------------------
  const [inventoryStats, setInventoryStats] = useState([
    { title: "Total Devices", value: "0", changeType: "neutral", iconColor: "#10164d" },
    { title: "In Warehouse", value: "0", changeType: "positive", iconColor: "#3b82f6" },
    { title: "Distributed", value: "0", changeType: "neutral", iconColor: "#06b6d4" },
    { title: "Yet to be Received", value: "0", changeType: "warning", iconColor: "#ef4444" },
  ])
  const [kpiLoading, setKpiLoading] = useState(false)

  // previous static KPIs (commented out as requested)
  /*
  const inventoryStats = [
    {
      title: "Total Devices",
      value: "150",
      changeType: "positive",
      iconColor: "#10164d",
    },
    {
      title: "Available Devices",
      value: "45",
      changeType: "positive",
      iconColor: "#3b82f6",
    },
    {
      title: "Assigned Devices",
      value: "85",
      changeType: "neutral",
      iconColor: "#06b6d4",
    },
    {
      title: "Warranty Expiring Soon",
      value: "5",
      changeType: "warning",
      iconColor: "#ef4444",
    },
  ]
  */

  // Chart data for admin view (deviceDistribution will be replaced from API)
  const defaultDeviceDistribution = [
    { name: "Kalioundji", value: 35, color: "#10B981" },
    { name: "Fakhrei", value: 25, color: "#3B82F6" },
    { name: "RCMG", value: 20, color: "#F59E0B" },
    { name: "Others", value: 20, color: "#8B5CF6" },
  ]

  const [deviceDistributionState, setDeviceDistributionState] = useState(defaultDeviceDistribution)
  const [metricsLoading, setMetricsLoading] = useState(false)

  // warehouseInventory chart data (will be replaced by API)
  const [warehouseInventoryChart, setWarehouseInventoryChart] = useState([
    { name: "Distributed", value: 14, color: "#87CEEB" },
    { name: "Yet to Receive", value: 30, color: "#F59E0B" },
    { name: "Warehouse", value: 30, color: "#10B981" },
    { name: "Refurbished", value: 26, color: "#8B5CF6" },
  ])

  const chartData = {
    refurbishedDevices: [
      { name: "TELE RPM", value: 45, color: "#10B981" },
      { name: "Tenovi", value: 35, color: "#3B82F6" },
      { name: "Other Devices", value: 20, color: "#F59E0B" },
    ],
    refurbishmentStatus: [
      { name: "Ready", value: 60, color: "#10B981" },
      { name: "Pending", value: 25, color: "#F59E0B" },
      { name: "Needs Warranty", value: 15, color: "#EF4444" },
    ],
    warehouseInventory: warehouseInventoryChart,
    deviceDistribution: deviceDistributionState,
    devicePurchaseWarranty: [
      { name: "RCMG", value: 40, color: "#10B981" },
      { name: "Shalita", value: 30, color: "#3B82F6" },
      { name: "Fakhrei", value: 20, color: "#F59E0B" },
      { name: "Dev", value: 10, color: "#8B5CF6" },
    ],
    warrantiesOnProducts: [
      { name: "Yes", value: 75, color: "#10B981" },
      { name: "No", value: 25, color: "#EF4444" },
    ],
    productType: [
      { name: "TELE RPM BPM", value: 40, color: "#10B981" },
      { name: "Cuff", value: 30, color: "#3B82F6" },
      { name: "Scale", value: 20, color: "#F59E0B" },
      { name: "Others", value: 10, color: "#8B5CF6" },
    ],
    claimsSummary: [
      { name: "Lesli", value: 25 },
      { name: "Sean", value: 20 },
      { name: "Kevin", value: 18 },
      { name: "Others", value: 15 },
    ],
  }

  // Admin orders data (all locations)
  const pendingOrdersData = [
    {
      id: 1,
      orderNumber: "ORD-2023-001",
      location: "Los Angeles",
      orderDate: "3-14-2023",
      devices: "40",
      status: "In Transit",
    },
    {
      id: 2,
      orderNumber: "ORD-2023-002",
      location: "New York",
      orderDate: "3-14-2023",
      devices: "25",
      status: "In Transit",
    },
    {
      id: 3,
      orderNumber: "ORD-2023-003",
      location: "Chicago",
      orderDate: "3-14-2023",
      devices: "15",
      status: "Done",
    },
    {
      id: 4,
      orderNumber: "ORD-2023-004",
      location: "Miami",
      orderDate: "3-14-2023",
      devices: "8",
      status: "Done",
    },
  ]

  const pendingOrdersColumns = [
    {
      key: "orderNumber",
      label: "Order #",
      alignLeft: true,
    },
    {
      key: "location",
      label: "Location",
      alignLeft: true,
    },
    {
      key: "orderDate",
      label: "Order Date",
      alignLeft: true,
      sorting: false,
    },
    {
      key: "devices",
      label: "Devices",
      alignLeft: true,
    },
    {
      key: "status",
      label: "Status",
      sorting: false,
      render: (row) => (
        <Badge
          variant={
            row.status === "delivered" ? "success" : row.status === "cancelled" ? "warning" : row.status === "pending" ? "warning" : "warning"
          }
          children={row.status}
        />
      ),
    },
  ]

  const mainTabs = [
    { id: "inventory", label: "Warehouse Overview" },
    { id: "refurbishment", label: "Refurbishment" },
    { id: "warranty", label: "Warranty & Purchases" },
    { id: "product", label: "Product & Claims" },
  ]

  const subTabs = {
    refurbishment: [
      { id: "refurbished-devices", label: "Refurbished Devices" },
      { id: "refurbishment-status", label: "Refurbishment Status" },
    ],
    inventory: [
      { id: "warehouse-inventory", label: "Warehouse Inventory" },
      { id: "device-distribution", label: "Device Distribution" },
    ],
    warranty: [
      { id: "device-purchase-warranty", label: "Device Purchase/Warranty Overview" },
      { id: "warranties-products", label: "Warranties on Products" },
    ],
    product: [
      { id: "product-type", label: "Product Type" },
      { id: "claims-summary", label: "Claims Summary" },
    ],
  }

  const handleViewFullInventory = () => {
    navigate("/inventory/supplies")
  }

  const handleOrderDevices = () => {
    setOrderInventoryModal({ isOpen: true })
  }

  const orderSectionTabs = [
    { id: "pending-orders", label: "Pending Bulk Orders" },
    { id: "dropship-requests", label: "Dropship Order Requests" }, // New tab
  ]
  const [activeOrderTab, setActiveOrderTab] = useState("pending-orders")

  // ---------------------------
  // Fetch warehouse stats and map to KPIs + charts
  // ---------------------------
  useEffect(() => {
    let mounted = true
    const loadStats = async () => {
      setKpiLoading(true)
      setMetricsLoading(true)
      try {
        const res = await getWarehouseStats()
        // res shape can be { total_Warehouse_devices, warehouse_stats: [...], tenants_stats: [...] }
        // or legacy array of tenants.
        const total = Number(res?.total_Warehouse_devices ?? 0)
        const warehouse_stats = Array.isArray(res?.warehouse_stats) ? res.warehouse_stats : []
        const tenants = Array.isArray(res?.tenants_stats) ? res.tenants_stats : Array.isArray(res) ? res : []

        // build warehouseInventory chart data from warehouse_stats
        const mappedWarehouseChart = warehouse_stats.map((s, idx) => ({
          name: s.inventory_status || `Status ${idx + 1}`,
          value: Number(s.count ?? 0),
        }))

        // pick three KPI statuses to show alongside total:
        // try to get counts for specific statuses (fallback to first three items)
        const getStatusCount = (statusName) => {
          const entry = warehouse_stats.find((w) => String(w.inventory_status).toLowerCase() === String(statusName).toLowerCase())
          return entry ? Number(entry.count ?? 0) : 0
        }

        const inWarehouseCount = getStatusCount("In Warehouse") || getStatusCount("In Warehouse (Refurbished)") || 0
        const distributedCount = getStatusCount("Distributed") || 0
        const yetToReceiveCount = getStatusCount("Yet to be Received") || getStatusCount("Yet to be received") || 0

        if (mounted) {
          setInventoryStats([
            { title: "Total Devices", value: String(total), changeType: "neutral", iconColor: "#10164d" },
            { title: "In Warehouse", value: String(inWarehouseCount), changeType: "positive", iconColor: "#3b82f6" },
            { title: "Distributed", value: String(distributedCount), changeType: "neutral", iconColor: "#06b6d4" },
            { title: "Yet to be Received", value: String(yetToReceiveCount), changeType: "warning", iconColor: "#ef4444" },
          ])

          // set warehouseInventory chart (if none, keep default)
          if (mappedWarehouseChart.length > 0) {
            setWarehouseInventoryChart(mappedWarehouseChart)
          }

          // optionally set deviceDistribution from tenants if available
          if (Array.isArray(tenants) && tenants.length > 0) {
            const mappedTenants = tenants.map((t, idx) => ({
              name: t?.tenant_name || t?.tenant || `Tenant ${idx + 1}`,
              value: Number(t?.devices?.total_devices ?? 0),
            }))
            if (mappedTenants.length > 0) setDeviceDistributionState(mappedTenants)
          }
        }
      } catch (err) {
        console.error("Failed to load warehouse stats:", err)
      } finally {
        if (mounted) {
          setKpiLoading(false)
          setMetricsLoading(false)
        }
      }
    }

    loadStats()
    return () => {
      mounted = false
    }
  }, [])

  return (
    <>
      <DashboardStats stats={inventoryStats} loading={kpiLoading} />

      <DashboardMetricsCard
        chartData={chartData}
        headerButtons={[
          {
            label: "View Full Inventory",
            variant: "secondary",
            onClick: handleViewFullInventory,
          },
          {
            label: "Order Devices",
            variant: "primary",
            onClick: handleOrderDevices,
          },
        ]}
        mainTabs={mainTabs}
        subTabs={subTabs}
        loading={metricsLoading}
      />

      {/* Orders and Dropship Requests Section */}
      <Card title={"Pending Orders"}>
        <div className={styles.section}>
          <div className={styles.tabs_container}>
            <Tabs
              tabs={orderSectionTabs}
              activeTab={activeOrderTab}
              onChange={setActiveOrderTab}
              variant="secondary"
              size="medium"
            />
          </div>
          {activeOrderTab === "pending-orders" && <PendingOrdersSection title="All Pending Orders" data={pendingOrdersData} columns={pendingOrdersColumns} />}
          {activeOrderTab === "dropship-requests" && <DropshipRequestsSection />}
        </div>
      </Card>

      {/* Order Inventory Modal */}
      {orderInventoryModal.isOpen && (
        <OrderInventory
          isOpen={orderInventoryModal.isOpen}
          setIsOpen={(isOpen) => setOrderInventoryModal({ isOpen })}
        />
      )}
    </>
  )
}

export default AdminDashboardView
